import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUnsettledBookingsAccountingReportQuery } from '../Service/reportsApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { BookingsRoutes, ReportsRoutes } from '../../../Router/path';
import type {
  BookingSettlementReadinessFilter,
  GetUnsettledBookingsAccountingReportParams,
} from '../Types/reports.types';
import type {
  BookingReturnToReportNavigationState,
  UnsettledBookingsAccountingReportFiltersSnapshot,
  UnsettledBookingsAccountingReportRestoreNavigationState,
} from '../../Shared/Types/reportsNavigationTypes';

const DEFAULT_PAGE_SIZE = 10;

export interface UnsettledBookingsAccountingReportSearchFilters {
  companyId?: number;
  bookingDateToFrom?: string;
  bookingDateToTo?: string;
  readiness: BookingSettlementReadinessFilter;
}

const defaultSearchFilters: UnsettledBookingsAccountingReportSearchFilters = {
  companyId: undefined,
  bookingDateToFrom: undefined,
  bookingDateToTo: undefined,
  readiness: 'all',
};

const toComparableDate = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value.slice(0, 10));
  return Number.isNaN(timestamp) ? null : timestamp;
};

const hasInvalidDateRange = (values: UnsettledBookingsAccountingReportSearchFilters): boolean => {
  const fromTimestamp = toComparableDate(values.bookingDateToFrom);
  const toTimestamp = toComparableDate(values.bookingDateToTo);

  if (fromTimestamp === null || toTimestamp === null) {
    return false;
  }

  return fromTimestamp > toTimestamp;
};

export const useUnsettledBookingsAccountingReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoredReportFilters = (
    location.state as UnsettledBookingsAccountingReportRestoreNavigationState | null
  )?.restoredReportFilters;

  const [draftFilters, setDraftFilters] =
    useState<UnsettledBookingsAccountingReportSearchFilters>(() =>
      restoredReportFilters
        ? {
            companyId: restoredReportFilters.companyId,
            bookingDateToFrom: restoredReportFilters.bookingDateToFrom,
            bookingDateToTo: restoredReportFilters.bookingDateToTo,
            readiness: restoredReportFilters.readiness,
          }
        : defaultSearchFilters
    );
  const [filters, setFilters] = useState<Partial<GetUnsettledBookingsAccountingReportParams>>(() =>
    restoredReportFilters
      ? { ...restoredReportFilters }
      : { ...defaultSearchFilters, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE }
  );
  const [validationError, setValidationError] = useState<string>('');

  const hasCompanyId = typeof filters.companyId === 'number' && filters.companyId > 0;

  const { data, isLoading, isFetching, isError, error } = useGetUnsettledBookingsAccountingReportQuery(
    hasCompanyId
      ? (filters as GetUnsettledBookingsAccountingReportParams)
      : { companyId: 0, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
    { skip: !hasCompanyId }
  );

  const queryErrorMessage = isError ? extractApiErrorMessage(error, 'Nie udało się pobrać raportu.') : null;

  const handleDraftFilterChange = useCallback(
    (next: Partial<UnsettledBookingsAccountingReportSearchFilters>) => {
      setDraftFilters((prev) => ({ ...prev, ...next }));
    },
    []
  );

  const handleSearchSubmit = useCallback(() => {
    if (hasInvalidDateRange(draftFilters)) {
      setValidationError(
        "Nieprawidłowy zakres dat: 'Zakończenie od' nie może być późniejsza niż 'Zakończenie do'."
      );
      return;
    }

    setValidationError('');
    setFilters((prev) => ({ ...prev, ...draftFilters, pageNumber: 1 }));
  }, [draftFilters]);

  const handleResetFilters = useCallback(() => {
    setValidationError('');
    setDraftFilters(defaultSearchFilters);
    setFilters((prev) => ({ ...prev, ...defaultSearchFilters, pageNumber: 1 }));
  }, []);

  const handlePageChange = useCallback((pageNumber: number) => {
    setFilters((prev) => ({ ...prev, pageNumber }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize, pageNumber: 1 }));
  }, []);

  const goToBookingSettlement = useCallback(
    (bookingId: number) => {
      const updatePath = BookingsRoutes.update.replace(':bookingId', bookingId.toString());
      const returnTo = encodeURIComponent(ReportsRoutes.unsettledBookingsAccounting);
      const reportReturnContext: UnsettledBookingsAccountingReportFiltersSnapshot = {
        companyId: filters.companyId,
        bookingDateToFrom: filters.bookingDateToFrom,
        bookingDateToTo: filters.bookingDateToTo,
        readiness: filters.readiness ?? 'all',
        pageNumber: filters.pageNumber ?? 1,
        pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
      };

      navigate(`${updatePath}?origin=unsettled-bookings-report&returnTo=${returnTo}`, {
        state: { reportReturnContext } satisfies BookingReturnToReportNavigationState,
      });
    },
    [navigate, filters]
  );

  return {
    data,
    isLoading,
    isFetching,
    queryErrorMessage,
    validationError,
    draftFilters,
    filters,
    hasCompanyId,
    handleDraftFilterChange,
    handleSearchSubmit,
    handleResetFilters,
    handlePageChange,
    handlePageSizeChange,
    goToBookingSettlement,
  };
};
